import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, User, LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  
  // Check if user is logged in
  const isLoggedIn = localStorage.getItem('authToken');
  const userType = localStorage.getItem('userType'); // 'candidate', 'employer', or 'admin'
  const userName = localStorage.getItem('userName');

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    navigate('/');
  };

  const getDashboardLink = () => {
    if (userType === 'candidate') return '/candidate/dashboard';
    if (userType === 'employer') return '/employer/dashboard';
    if (userType === 'admin') return '/admin/dashboard';
    return '/';
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 ${isRTL ? 'rtl' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center h-20 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Logo */}
          <Link to="/" className={`flex items-center space-x-3 group ${isRTL ? 'space-x-reverse' : ''}`}>
            <img 
              src="https://customer-assets.emergentagent.com/job_7ed38909-6cf6-4893-90cd-163f5e50715b/artifacts/zwzpuwf4_logo%20svg.svg" 
              alt="Luma Med Global" 
              className="h-12 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className={`hidden lg:flex items-center space-x-8 ${isRTL ? 'space-x-reverse' : ''}`}>
            <Link to="/about" className="text-gray-700 hover:text-[#42b1c2] transition-colors font-medium">
              {t('nav.about')}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-gray-700 hover:text-[#42b1c2] transition-colors font-medium flex items-center">
                {t('nav.howItWorks')}
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem asChild>
                  <Link to="/how-it-works/candidates">{t('nav.forCandidates')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/how-it-works/employers">{t('nav.forEmployers')}</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/pricing" className="text-gray-700 hover:text-[#42b1c2] transition-colors font-medium">
              {t('nav.pricing')}
            </Link>
            <Link to="/faq" className="text-gray-700 hover:text-[#42b1c2] transition-colors font-medium">
              {t('nav.faq')}
            </Link>
            <Link to="/contact" className="text-gray-700 hover:text-[#42b1c2] transition-colors font-medium">
              {t('nav.contact')}
            </Link>
          </div>

          {/* Right Side Actions */}
          <div className={`hidden lg:flex items-center space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
            {/* Language Toggle */}
            <LanguageSwitcher />

            {isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                    <User className="h-4 w-4" />
                    <span>{userName || 'User'}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()}>Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-700 hover:text-[#42b1c2]">
                    Login
                  </Button>
                </Link>
                <Link to="/join-talent-pool">
                  <Button className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white">
                    {t('nav.uploadCV')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-600 hover:text-[#42b1c2] transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className={`lg:hidden bg-white border-t border-gray-200 ${isRTL ? 'text-right' : ''}`}>
          <div className="px-4 py-4 space-y-3">
            {/* Language Switcher for Mobile */}
            <div className="py-2 border-b border-gray-100 mb-2">
              <LanguageSwitcher />
            </div>
            <Link to="/about" className="block py-2 text-gray-700 hover:text-[#42b1c2] transition-colors">
              {t('nav.about')}
            </Link>
            <Link to="/how-it-works/candidates" className="block py-2 text-gray-700 hover:text-[#42b1c2] transition-colors">
              {t('nav.forCandidates')}
            </Link>
            <Link to="/how-it-works/employers" className="block py-2 text-gray-700 hover:text-[#42b1c2] transition-colors">
              {t('nav.forEmployers')}
            </Link>
            <Link to="/pricing" className="block py-2 text-gray-700 hover:text-[#42b1c2] transition-colors">
              {t('nav.pricing')}
            </Link>
            <Link to="/faq" className="block py-2 text-gray-700 hover:text-[#42b1c2] transition-colors">
              {t('nav.faq')}
            </Link>
            <Link to="/contact" className="block py-2 text-gray-700 hover:text-[#42b1c2] transition-colors">
              {t('nav.contact')}
            </Link>
            <div className="pt-4 border-t border-gray-200 space-y-2">
              {isLoggedIn ? (
                <>
                  <Link to={getDashboardLink()}>
                    <Button className="w-full bg-[#42b1c2] hover:bg-[#3a9fb0] text-white">
                      Dashboard
                    </Button>
                  </Link>
                  <Button onClick={handleLogout} variant="outline" className="w-full">
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="outline" className="w-full">
                      {t('nav.login')}
                    </Button>
                  </Link>
                  <Link to="/join-talent-pool">
                    <Button className="w-full bg-[#42b1c2] hover:bg-[#3a9fb0] text-white">
                      {t('nav.uploadCV')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;